/**
 * PushManagerContainer
 * --
 *
 */

import React, { useState } from 'react';
import PushManagerPresenter from './PushManagerPresenter';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import { Spin } from 'antd';
import { useLocation, useParams } from 'react-router-dom';
import { useBreadcrumbAction } from 'store';
import { useGetUsers } from 'hooks/UserHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import { useSendEmail, useSendPushMessage } from 'hooks/SendHooks';
import {
  useBizFileUpload,
  useBizSendLMS,
  useBizSendMMS,
  useBizSendSMS,
} from 'hooks/BizHooks';
import { useGetPushTemplates } from 'hooks/PushTemplateHooks';
import { useGetEmailTemplates } from 'hooks/EmailTemplateHooks';
import { useGetSMSTemplates } from 'hooks/SMSTemplateHooks';
const TYPE_TEXT = {
  push: '푸시알림',
  sms: '문자',
  email: '이메일',
};
const INITIAL_VALUES = {
  'push-manager': {
    title: '',
    body: '',
  },
  'sms-manager': { subject: '', message: '' },
  'email-manager': { subject: '', content: '' },
};
/**----------------------------------------
 * [Component] PushManagerContainer
 * ----------------------------------------
 */
const PushManagerContainer = () => {
  /* ===== Props ===== */

  /* ===== ROUTE ===== */
  const { messageType } = useParams();
  const location = useLocation();
  const { users: selectUsers } = location.state || {};

  /* ===== Store ===== */
  const breadcrumbAction = useBreadcrumbAction();

  /* ===== Mutate ===== */
  const { sendEmail } = useSendEmail();
  const { sendLMS } = useBizSendLMS();
  const { sendSMS } = useBizSendSMS();
  const { sendMMS } = useBizSendMMS();
  const { sendPushMessage } = useSendPushMessage();
  const options = {
    select: (res) => {
      const { status, message, data } = res;

      if (status !== 200) {
        console.warn(message);
        return [];
      }

      return data;
    },
    initialData: [],
  };
  const query = {};
  const { data: EmailTemplates } = useGetEmailTemplates(query, options);
  const { data: SMSTemplates } = useGetSMSTemplates(query, options);
  const { data: PushTemplates } = useGetPushTemplates(query, options);
  const { fileUpload } = useBizFileUpload();

  /* ===== PROPS ===== */
  /* ===== Data ===== */
  // 등급 데이터
  const { data: grades } = useGetGrades(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });

  // 회원 데이터
  const { data: users } = useGetUsers(
    {
      join_tables: '["grade", "device"]',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    },
  );

  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [sendData, setSendData] = useState(INITIAL_VALUES[messageType] || {});

  // 선택회원
  const [selectedUsers, setSelectedUsers] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**--------------------
   * 데이터 변경
   * --------------------
   */
  const handleChangeData = (key, value) => {
    setSendData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * 메세지 발송 유효성 검사
   * --
   * @param {string} type
   * @returns {boolean}
   */
  const handleValidateCheck = async (type) => {
    if (!selectedUsers.length) {
      MessageAlert.info('메세지를 전송할 회원을 선택해주세요!');
      return null;
    }

    const validateType = type.toLocaleUpperCase();

    switch (validateType) {
      case 'SMS':
        if (getEucKrByteLength(sendData?.message) > 90) {
          MessageAlert.info('SMS 내용은 90바이트를 초과할 수 없습니다.');
          return null;
        }

        return {
          content: {
            sms: {
              message: sendData?.message,
            },
          },
          is_template: 'N',
          type: 'sms',
          to_phones: selectedUsers.map((user) => user.sms),
        };
      case 'LMS':
        if (getEucKrByteLength(sendData?.subject) > 64) {
          MessageAlert.info('LMS 내용은 64바이트를 초과할 수 없습니다.');
          return null;
        }

        if (getEucKrByteLength(sendData?.message) > 2000) {
          MessageAlert.info('LMS 내용은 2,000바이트를 초과할 수 없습니다.');
          return null;
        }
        return {
          content: {
            lms: {
              subject: sendData?.subject,
              message: sendData?.message,
            },
          },
          is_template: 'N',
          type: 'lms',
          to_phones: selectedUsers.map((user) => user.sms),
        };
      case 'MMS':
        if (getEucKrByteLength(sendData?.subject) > 64) {
          MessageAlert.info('MMS 내용은 64바이트를 초과할 수 없습니다.');
          return null;
        }

        if (getEucKrByteLength(sendData?.message) > 2000) {
          MessageAlert.info('MMS 내용은 2,000바이트를 초과할 수 없습니다.');
          return null;
        }

        if (!sendData?.file) {
          MessageAlert.info('파일을 업로드해주세요!');
          return null;
        }

        const formData = new FormData();

        formData.append('file', sendData?.file);

        // const { status, data } = await fileUpload(formData);
        const { status, data } = await fileUpload({
          file: sendData?.file,
        });

        if (status !== 200) {
          MessageAlert.info('파일 업로드에 실패하였습니다!');
          return null;
        }

        return {
          content: {
            mms: {
              subject: sendData?.subject,
              message: sendData?.message,
              file: [
                {
                  type: 'IMG',
                  key: data?.filekey,
                },
              ],
            },
          },
          is_template: 'N',
          type: 'mms',
          to_phones: selectedUsers.map((user) => user.sms),
        };
      case 'EMAIL':
        if (!sendData?.subject) {
          MessageAlert.info('이메일 제목을 입력해주세요.');
          return null;
        }

        if (sendData?.subject > 100) {
          MessageAlert.info('이메일 제목은 100자내로 입력해주세요.');
          return null;
        }

        if (!sendData?.content) {
          MessageAlert.info('이메일 내용을 입력해주세요.');
          return null;
        }

        return {
          subject: sendData?.subject,
          content: sendData?.content,
          is_template: 'N',
          to_emails: selectedUsers.map((user) => user.email),
        };
      case 'PUSH':
        if (!sendData?.title) {
          MessageAlert.info('푸시알림 제목을 입력해주세요.');
          return null;
        }

        if (getEucKrByteLength(sendData?.title) > 200) {
          MessageAlert.info('푸시알림 제목은 200byte내로 입력해주세요.');
          return null;
        }

        if (!sendData?.body) {
          MessageAlert.info('푸시알림 내용을 입력해주세요.');
          return null;
        }

        if (getEucKrByteLength(sendData?.body) > 1000) {
          MessageAlert.info('푸시알림 내용은 1000byte내로 입력해주세요.');
          return null;
        }
        /**
         * 디바이스 토큰이 없는 유저 제외
         */
        const checkDeviceTokens = selectedUsers
          .filter((user) => user.push)
          .map((user) => user.push);

        return {
          title: sendData?.title,
          body: sendData?.body,
          device_tokens: checkDeviceTokens,
        };
      case 'ALIM':
        return null;
      default:
        MessageAlert.info('메세지 발송에 실패하였습니다.');
        return null;
    }
  };

  /**
   * 메세지 발송
   * --
   */
  const handleSubmit = async () => {
    let submitType;

    const splitType = messageType.split('-')[0];

    if (splitType === 'sms') {
      submitType = sendData.type ?? 'sms';
    } else {
      submitType = splitType;
    }

    // 메세지 발송 전 유효성 검사
    const submitData = await handleValidateCheck(submitType);
    if (!submitData) return;

    try {
      setLoading(true);
      const confirm = window.confirm(
        `${stringToMoneyFormat(
          selectedUsers.length,
          '명',
        )}에게 메세지를 전송하시겠습니까?`,
      );

      if (!confirm) {
        setLoading(false);
        return;
      }

      const mutate = {
        sms: sendSMS,
        lms: sendLMS,
        mms: sendMMS,
        email: sendEmail,
        push: sendPushMessage,
      };

      const { status, data } = await mutate[submitType](submitData);

      if (status !== 200) {
        MessageAlert.warning('메세지 발송에 실패하였습니다!');
        return;
      }
      let plusFailPush =
        splitType === 'push'
          ? selectedUsers.filter((item) => !item.push).length
          : 0;
      MessageAlert.success(
        `${stringToMoneyFormat(
          data.success.length,
        )}건 전송 성공, ${stringToMoneyFormat(
          data.failure.length + plusFailPush,
        )}건 전송 실패`,
      );
      plusFailPush = 0;
    } catch (err) {
      console.error('[handleSubmit] Error!! ', err);
      MessageAlert.error('메세지 발송에 실패하였습니다!');
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedUsers((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedUsers((prev) =>
          prev.filter((select) => select.key !== obj.key),
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedUsers(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedUsers.filter((user) =>
          obj.some((ob) => ob.key === user.key),
        );

        setSelectedUsers(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedUsers.length === obj.length;

        if (!isAll) {
          setSelectedUsers(obj);
        } else {
          setSelectedUsers([]);
        }
        return;
      default:
        return;
    }
  };

  /**
   * 간단한 EUC-KR 바이트 수 계산 함수 (단순 추정)
   * ASCII (0~127): 1바이트, 그 외: 2바이트로 가정
   * 주의: 모든 경우에 정확하지 않을 수 있음
   */
  function getEucKrByteLength(str) {
    let byteLength = 0;
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      // ASCII 코드 범위는 0~127
      if (code <= 0x7f) {
        byteLength += 1;
      } else {
        // 한글 등은 일반적으로 2바이트로 처리 (EUC-KR)
        byteLength += 2;
      }
    }
    return byteLength;
  }

  /* ===== HOOKS ===== */
  /**
   * 회원 데이터 정제
   * --
   */
  const Users = React.useMemo(() => {
    if (!users.length || !messageType) return [];

    const newUsers = users.map((user) => {
      const {
        user_phone,
        user_email,
        device,
        user_setting,
        grade,
        user_account,
        social_id,
      } = user;

      // 모바일 토큰 추출
      const findMobileDevice = device
        ? device.find((token) => token.type === 'MOBILE')
        : null;

      return {
        ...user,
        key: user.user_id,
        push: findMobileDevice ? findMobileDevice.push_token : null,
        sms: user_phone,
        email: user_email,
        grade_name: grade?.grade_name,
        is_member: user_account || social_id ? 'Y' : 'N',
        push_notification: user_setting?.push_notification ? 'Y' : ('N' ?? 'N'), // 푸시알림 동의 여부
        event_notification: user_setting?.event_notification
          ? 'Y'
          : ('N' ?? 'N'), // 프로모션/이벤트 알림 동의 여부
        marketing_consent: user_setting?.marketing_consent ? 'Y' : ('N' ?? 'N'), // 마케팅정보 수집 및 활용 동의 여부
        terms_of_ad: user_setting?.terms_of_ad ? 'Y' : ('N' ?? 'N'), // 혜택 및 광고성 정보 수신 동의 여부
        terms_of_marketing: user_setting?.terms_of_marketing
          ? 'Y'
          : ('N' ?? 'N'), // 마케팅 목적의 개전정보 수집 및 이용 동의 여부
      };
    });

    const splitType = messageType.split('-');
    // 타입(push, email, sms)에 맞게 데이터가 있는 회원만 필터
    console.log(splitType);
    const filterData = newUsers.filter((user) => user[splitType[0]]);

    // const filterData = newUsers;

    return filterData;
  }, [users, messageType]);

  /**
   * 등급 데이터 정제
   * --
   */
  const Grades = React.useMemo(() => {
    if (!grades.length) return [];

    const newGrades = grades.map((grade) => ({
      label: grade.grade_name,
      value: grade.grade_id,
    }));

    return newGrades;
  }, [grades]);

  /**
   * 광고(ADVERTISEMENT) 템플릿 활성화 체크
   * is_enable
   * Y -> true
   * N -> false
   */
  const handleValidateCheckTemplates = () => {
    const findTemplateDate = PushTemplates.find(
      (item) => item.type === 'ADVERTISEMENT',
    );
    if (findTemplateDate.is_enable === 'Y') {
      return true;
    }
    return false;
  };
  /**
   * 템플릿 데이터 정제
   * --
   */
  const templates = React.useMemo(() => {
    const template = {
      'push-manager': {},
      'sms-manager': {},
      'email-manager': {},
    };
    // const INITIAL_VALUES = {
    //   'push-manager': {
    //     title: '',
    //     body: '',
    //   },
    //   'sms-manager': { subject: '', message: '' },
    //   'email-manager': { subject: '', content: '' },
    // };
    const type = 'ADVERTISEMENT';
    PushTemplates.forEach((item) => {
      if (item.type === type) {
        template['push-manager'] = item?.push_content;
      }
    });
    SMSTemplates.forEach((item) => {
      if (item.type === type) {
        const text = item?.sms_content;
        let openCount = 0;
        let titleEndIndex = -1;

        // 바깥쪽 대괄호 닫히는 위치 찾기
        for (let i = 0; i < text.length; i++) {
          const char = text[i];

          if (char === '[') openCount++;
          if (char === ']') openCount--;

          if (openCount === 0 && titleEndIndex === -1) {
            titleEndIndex = i;
            break;
          }
        }

        let rawTitle = text.slice(0, titleEndIndex + 1); // [..] 포함
        const content = text.slice(titleEndIndex + 1).trim(); // 이후 문자열

        // 제목에서 바깥쪽 [ ] 제거
        if (rawTitle.startsWith('[') && rawTitle.endsWith(']')) {
          rawTitle = rawTitle.slice(1, -1);
        }
        template['sms-manager'] = {
          subject: rawTitle.trim(),
          message: content,
        };
      }
    });
    EmailTemplates.forEach((item) => {
      if (item.type === type) {
        template['email-manager'] = item?.email_content;
      }
    });
    return template;
  }, [EmailTemplates, SMSTemplates, PushTemplates]);

  /**
   * 헤더 상단 Breadcrumb 설정
   * --
   */
  React.useEffect(() => {
    if (!messageType) return;

    const split = messageType.split('-');

    const pathName = [
      { title: '메시지 관리' },
      { title: `${TYPE_TEXT[split[0]]} 발송` },
    ];

    breadcrumbAction.push(pathName);
    // eslint-disable-next-line
  }, [messageType]);

  React.useEffect(() => {
    if (!selectUsers || !selectUsers?.length) return;

    const selectedUserList =
      selectUsers?.map((_item) => {
        const findUser = Users?.find((user) => user?.user_id === _item?.key);

        return {
          key: findUser?.user_id,
          ...findUser,
        };
      }) ?? [];

    setSelectedUsers(selectedUserList);
  }, [selectUsers, Users]);

  /**
   * 광고 템플릿 적용
   * --
   */
  const handleAddADTemplate = (t) => {
    if (!handleValidateCheckTemplates())
      return MessageAlert.info('광고 템플릿이 비활성화 상태입니다.');
    if (!t) return MessageAlert.info('적용 가능한 광고 템플릿이 없습니다..');
    setSendData(templates[t]);
  };
  /* ===== RENDER ===== */
  const props = {
    sendData,
    onChangeData: handleChangeData,
    onAddADTemplate: handleAddADTemplate,
  };

  return (
    <>
      <Spin spinning={loading}>
        <PushManagerPresenter
          type={messageType}
          typeText={TYPE_TEXT}
          users={Users}
          grades={Grades}
          setLoading={setLoading}
          selectedUsers={selectedUsers}
          onSelect={handleSelect}
          onSubmit={handleSubmit}
          getEucKrByteLength={getEucKrByteLength}
          {...props}
        />
      </Spin>
    </>
  );
};

export default PushManagerContainer;
